import type { ThemeColor } from '@sites/site-theme/src/types/theme-color.type';
import type { ThemeSelectedFonts } from '@sites/site-theme/src/types/theme-font.type';

import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';

/**
 * Every tag that carries a text colour. `button` does not: a button's text colour belongs to the
 * button theme.
 */
function carriesColor(tag: ThemeFontTags): boolean {
    return tag !== ThemeFontTags.button;
}

/**
 * Gives every tag with no stored colour a desktop one, which tablet and mobile then inherit.
 *
 * A colour already stored at any breakpoint wins untouched. Editor-only, on purpose: the published
 * page keeps rendering a site that never saved a colour exactly as before the property existed —
 * no variable, so the text inherits — until the user saves it from the editor.
 */
export function withFontColorDefault(fonts: ThemeSelectedFonts, defaultColor: string): ThemeSelectedFonts {
    return {
        ...fonts,
        selected: fonts.selected.map((font) =>
            carriesColor(font.tag) && !font.color ? { ...font, color: { desktop: defaultColor } } : font
        ),
    };
}

/**
 * Re-points every tag's text colour at the palette's `dark`, for when a colour THEME is picked.
 *
 * Stored as the name rather than the value, so the formatter emits `var(--dark-color)` and the
 * text keeps following later edits to that colour. A palette without `dark` leaves the fonts alone.
 */
export function withPaletteFontColor(fonts: ThemeSelectedFonts, colors: ThemeColor[]): ThemeSelectedFonts {
    if (!colors.some((color) => color.name === ThemeColorNames.dark)) {
        return fonts;
    }

    return {
        ...fonts,
        selected: fonts.selected.map((font) =>
            carriesColor(font.tag) ? { ...font, color: { desktop: ThemeColorNames.dark } } : font
        ),
    };
}
